// ============================================================
//  OpenTabsContext — SAP-style Multi-Page Tab Manager
//  When user navigates to a new page, it's added to the tab bar.
//  Previous tabs stay visible at the bottom (minimized).
//  Click a tab to switch; close button removes it.
// ============================================================
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export interface OpenTab {
  path: string;      // full URL path
  title: string;     // tab label (page title)
  icon?: string;     // emoji/icon
  color?: string;    // accent color
  openedAt: number;  // timestamp
}

interface OpenTabsCtxValue {
  tabs: OpenTab[];
  activePath: string;
  openTab: (tab: Omit<OpenTab, "openedAt">) => void;
  closeTab: (path: string) => void;
  closeAllTabs: () => void;
  closeOtherTabs: (keepPath: string) => void;
  switchTab: (path: string) => void;
  setTabMeta: (path: string, meta: Partial<OpenTab>) => void;
}

const OpenTabsCtx = createContext<OpenTabsCtxValue>({
  tabs: [], activePath: "",
  openTab: () => {},
  closeTab: () => {},
  closeAllTabs: () => {},
  closeOtherTabs: () => {},
  switchTab: () => {},
  setTabMeta: () => {},
});

export const useOpenTabs = () => useContext(OpenTabsCtx);

// Routes that should NOT be tracked (login page, full-screen portals)
const EXCLUDED_PATHS = ["/login", "/portal", "/portal/home", "/sfc", "/sfc/station"];
const isExcluded = (p: string) => EXCLUDED_PATHS.some(ep => p === ep || p.startsWith(ep + "/"));

// Derive a default title from the URL path.
// For nested paths like /pricing/dashboard, join the parent segment so the tab
// reads "Pricing Dashboard" (not just "Dashboard"). Known routes get friendlier
// names via the explicit map below.
const TITLE_MAP: Record<string, string> = {
  "/": "Dashboard",
  "/dashboard": "Dashboard",
  // ── Pricing ────────────────────────────────────────
  "/pricing/dashboard":      "Pricing Dashboard",
  "/pricing/categories":     "Pricing Categories",
  "/pricing/commodity":      "Commodity Rates",
  "/pricing/cost-sheet":     "Cost Sheet",
  "/pricing/rate-sheet":     "Rate Sheet",
  "/pricing/customer":       "Customer Overrides",
  "/pricing/production-cost": "Production Cost",
  "/pricing/bulk":           "Bulk Operations",
  "/pricing/currency":       "Currency Rates",
  "/pricing/performance":    "Sales Performance",
  "/pricing/below-cost":     "Below-Cost Log",
  // ── Purchase ───────────────────────────────────────
  "/purchase/requisition":     "Purchase Requisition",
  "/purchase/orders":          "Purchase Orders",
  "/purchase/return":          "Purchase Return",
  "/purchase/ap-invoice":      "AP Invoice",
  "/purchase/vendor-payment":  "Vendor Payment",
  "/purchase/three-way-match": "3-Way Match",
  "/purchase/gstr2b":          "GSTR-2B Reconciliation",
  "/purchase/rfq":             "RFQ (Request for Quotation)",
  "/purchase/grn":             "Goods Receipt (GRN)",
  "/purchase/reports":         "Purchase Reports",
  // ── Sales ──────────────────────────────────────────
  "/sales/orders":             "Sales Orders",
  "/sales/quotation":          "Sales Quotations",
  "/sales/quotations":         "Sales Quotations",
  "/sales/delivery":           "Delivery",
  "/sales/ar-invoice":         "AR Invoice",
  "/sales/customer-payment":   "Customer Payment",
  "/sales/return":             "Sales Return",
  "/sales/buyback":            "Battery Buyback",
  "/sales/tracker":            "Sale Tracker",
  "/sales/sale-tracker":       "Sale Tracker — Battery Lifecycle",
  "/sales/dispatch":           "Dispatch Scan",
  "/sales/warranty":           "Warranty Claims — Battery RCA",
  "/sales/warranty-claims":    "Warranty Claims — Battery RCA",
  "/sales/e-invoice":          "E-Invoice (IRN)",
  "/sales/eway-bill":          "E-Way Bill",
  "/sales/credit":             "Credit Management",
  "/sales/followup":           "Payment Follow-up",
  "/sales/targets":            "Sales Targets",
  // ── Inventory ──────────────────────────────────────
  "/inventory/items":          "Item Master",
  "/inventory/warehouse":      "Warehouses",
  "/inventory/grn":            "Stock In (GRN)",
  "/inventory/ledger":         "Stock Ledger",
  "/inventory/gi":             "Good Issue / GR",
  "/inventory/stock-valuation":"Stock Valuation",
  "/inventory/stock-transfer": "Stock Transfer",
  "/inventory/wms":            "Warehouse (WMS)",
  "/inventory/physical-count": "Physical Count",
  "/inventory/stock-finance-reconciliation": "Stock & Finance Reconciliation",
};

const titleCase = (s: string) =>
  s.replace(/-/g, " ").split(" ")
   .map(w => w.charAt(0).toUpperCase() + w.slice(1))
   .join(" ");

const pathToTitle = (p: string): string => {
  // Explicit overrides first
  if (TITLE_MAP[p]) return TITLE_MAP[p];
  const parts = p.split("/").filter(Boolean);
  if (!parts.length) return "Home";
  // For 2-segment paths (e.g. /inventory/items → "Inventory Items"), include parent.
  if (parts.length >= 2) {
    return titleCase(parts[parts.length - 2]) + " " + titleCase(parts[parts.length - 1]);
  }
  return titleCase(parts[parts.length - 1]);
};

// Derive icon from path segment
const pathToIcon = (p: string): string => {
  if (p === "/" || p === "/dashboard") return "🏠";
  const seg = p.split("/")[1] || "";
  const map: Record<string, string> = {
    inventory: "📦", purchase: "🛒", sales: "💰", production: "🏭",
    qc: "✅", pm: "🔧", oee: "📈", serial: "🔋",
    hazmat: "⚗️", service: "🛠", "sfc-admin": "🏭",
    cost: "💵", dms: "📁", workflow: "⚡",
    bi: "📊", mc: "🏢", "portal-admin": "👥",
    finance: "💳", hr: "👥", crm: "📞", dealer: "🚚",
    master: "📋", users: "👤", audit: "📜", settings: "⚙️",
    alerts: "🔔", approval: "✔️", mis: "📑",
  };
  return map[seg] || "📄";
};

export const OpenTabsProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Load persisted tabs from sessionStorage (so refresh keeps tabs)
  const [tabs, setTabs] = useState<OpenTab[]>(() => {
    try {
      const raw = sessionStorage.getItem("erp_open_tabs");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  // Persist on change
  useEffect(() => {
    try { sessionStorage.setItem("erp_open_tabs", JSON.stringify(tabs)); } catch {}
  }, [tabs]);

  // Auto-add current path to tabs on route change
  useEffect(() => {
    const p = location.pathname;
    if (isExcluded(p)) return;
    setTabs(prev => {
      if (prev.some(t => t.path === p)) return prev;
      return [...prev, {
        path: p,
        title: pathToTitle(p),
        icon: pathToIcon(p),
        openedAt: Date.now(),
      }];
    });
  }, [location.pathname]);

  const openTab = useCallback((tab: Omit<OpenTab, "openedAt">) => {
    setTabs(prev => {
      if (prev.some(t => t.path === tab.path)) return prev;
      return [...prev, { ...tab, openedAt: Date.now() }];
    });
    navigate(tab.path);
  }, [navigate]);

  const closeTab = useCallback((path: string) => {
    const idx = tabs.findIndex(t => t.path === path);
    if (idx === -1) return;

    const nextTabs = tabs.filter(t => t.path !== path);
    setTabs(nextTabs);

    // Keep navigation outside the state updater. React Router updates state during
    // navigate(), and nesting that inside setTabs can trigger update-depth loops.
    if (location.pathname === path) {
      const fallback = nextTabs[Math.min(idx, nextTabs.length - 1)];
      navigate(fallback?.path || "/dashboard");
    }
  }, [location.pathname, navigate, tabs]);

  const closeAllTabs = useCallback(() => {
    setTabs([]);
    navigate("/dashboard");
  }, [navigate]);

  const closeOtherTabs = useCallback((keepPath: string) => {
    setTabs(prev => prev.filter(t => t.path === keepPath));
    if (location.pathname !== keepPath) navigate(keepPath);
  }, [location.pathname, navigate]);

  const switchTab = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  const setTabMeta = useCallback((path: string, meta: Partial<OpenTab>) => {
    setTabs(prev => prev.map(t => t.path === path ? { ...t, ...meta } : t));
  }, []);

  return (
    <OpenTabsCtx.Provider value={{
      tabs, activePath: location.pathname,
      openTab, closeTab, closeAllTabs, closeOtherTabs, switchTab, setTabMeta,
    }}>
      {children}
    </OpenTabsCtx.Provider>
  );
};

export default OpenTabsCtx;
